import Cookies from 'js-cookie';

/*
 * Configuration values needed in js codes
 *
 * NOTE:
 * Please use the following command to avoid accidentally committing personal changes
 * git update-index --assume-unchanged packages/shared/src/utils/config.js
 *
 */

export const livechat_license_id = 12049137;
export const livechat_client_id = '66aa088aad5a414484c1fd1fa8a5ace7';

export const domain_app_ids = {
    // these domains as supported "production domains"
    'deriv.app': 16929, // TODO: [app-link-refactor] - Remove backwards compatibility for `deriv.app`
    'app.deriv.com': 16929,
    'staging-app.deriv.com': 16303,
    'app.deriv.me': 1411,
    'staging-app.deriv.me': 1411, // TODO: setup staging for deriv.me
    'app.deriv.be': 30767,
    'staging-app.deriv.be': 31186,
    'binary.com': 1,
    'test-app.deriv.com': 51072,
};

export const platform_app_ids = {
    derivgo: 23789,
};

export const getCurrentProductionDomain = () =>
    !/^staging\./.test(window.location.hostname) &&
    Object.keys(domain_app_ids).find(domain => window.location.hostname === domain);

export const isProduction = () => {
    const all_domains = Object.keys(domain_app_ids).map(domain => `(www\\.)?${domain.replace('.', '\\.')}`);
    return new RegExp(`^(${all_domains.join('|')})$`, 'i').test(window.location.hostname);
};

export const isTestLink = () => {
    return /^((.*)\.binary\.sx)$/i.test(window.location.hostname);
};

export const isLocal = () => /localhost(:\d+)?$/i.test(window.location.hostname);

const STORAGE_KEY = 'config.app_id';

/** Must match root `src/utils/everyman-auth-cookies.ts` EVERYMAN_DTRADER_SYNC_COOKIE_NAME */
export const EVERYMAN_DTRADER_SYNC_COOKIE_NAME = 'everyman_dtrader_sync';

/** Numeric APP_ID from parent build (written to `config.app_id`). */
const EVERYMAN_SYNC_APP_ID_KEY = 'everyman_sync_app_id';
/** OAUTH_CLIENT_ID from parent build (PKCE client id string). */
const EVERYMAN_SYNC_OAUTH_CLIENT_ID_KEY = 'everyman_sync_oauth_client_id';
/**
 * Numeric **Deriv-App-ID** for V2 Options REST + OTP WS (matches PU `getOAuthDerivAppIdForEverymanSync`).
 * Separate from legacy WS `app_id` when `OAUTH_DERIV_APP_ID` differs from `APP_ID`.
 */
export const EVERYMAN_SYNC_OAUTH_DERIV_APP_ID_KEY = 'everyman_sync_oauth_deriv_app_id';

/**
 * Sync cookie **`v: 2`** = format revision only (alias **`v: 3`** still parsed). **`auth_flow`** = legacy vs oauth2 API.
 */
export type TEverymanDtraderSyncPayload =
    | {
          v: 2;
          auth_flow: 'legacy';
          active_loginid: string;
          app_id: string;
          ts: number;
      }
    | {
          v: 2;
          auth_flow: 'oauth2';
          active_loginid: string;
          oauth_client_id: string;
          deriv_app_id: string;
          ts: number;
      };

function parseEverymanSyncCookieRaw(raw: string): TEverymanDtraderSyncPayload | null {
    try {
        const o = JSON.parse(raw) as Record<string, unknown>;
        if (!o) return null;

        const rev = o.v;
        if (rev !== 2 && rev !== 3) return null;

        if (typeof o.active_loginid !== 'string' || !o.active_loginid) return null;
        if (o.auth_flow !== 'legacy' && o.auth_flow !== 'oauth2') return null;
        const ts = typeof o.ts === 'number' ? o.ts : Date.now();

        if (o.auth_flow === 'legacy') {
            const app_id = typeof o.app_id === 'string' ? o.app_id.trim() : '';
            if (!/^\d+$/.test(app_id)) return null;
            if (typeof o.oauth_client_id === 'string' && o.oauth_client_id.trim() !== '') return null;
            if (typeof o.deriv_app_id === 'string' && o.deriv_app_id.trim() !== '') return null;
            return { v: 2, auth_flow: 'legacy', active_loginid: o.active_loginid, app_id, ts };
        }

        const oauth_client_id =
            typeof o.oauth_client_id === 'string' ? o.oauth_client_id.trim() : '';
        if (!oauth_client_id) return null;

        const fromOldDeriv =
            typeof o.oauth_deriv_app_id === 'string' && /^\d+$/.test(o.oauth_deriv_app_id.trim())
                ? o.oauth_deriv_app_id.trim()
                : '';
        const fromDerivField =
            typeof o.deriv_app_id === 'string' && /^\d+$/.test(o.deriv_app_id.trim())
                ? o.deriv_app_id.trim()
                : '';
        const fromLegacyAppIdSlot =
            typeof o.app_id === 'string' && /^\d+$/.test(o.app_id.trim()) ? o.app_id.trim() : '';
        const deriv_app_id = fromOldDeriv || fromDerivField || fromLegacyAppIdSlot;
        if (!/^\d+$/.test(deriv_app_id)) return null;

        return {
            v: 2,
            auth_flow: 'oauth2',
            active_loginid: o.active_loginid,
            oauth_client_id,
            deriv_app_id,
            ts,
        };
    } catch {
        return null;
    }
}

/** True on *.everymanmarkets.site — app id must come from sync cookie only (no hostname/env fallback). */
export const isEverymanDeploymentHost = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        return window.location.hostname.includes('everymanmarkets.site');
    } catch {
        return false;
    }
};

/**
 * Read Everyman root sync cookie (`v: 2`, or deprecated `v: 3` alias). Legacy sets numeric **APP_ID**; oauth2 sets
 * **OAUTH_CLIENT_ID** + **Deriv-App-ID** and clears the opposite flow’s keys.
 */
export const applyEverymanSyncCookieToLocalStorage = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        const raw = Cookies.get(EVERYMAN_DTRADER_SYNC_COOKIE_NAME);
        if (!raw) return false;
        const parsed = parseEverymanSyncCookieRaw(raw);
        if (!parsed) return false;

        if (parsed.auth_flow === 'legacy') {
            localStorage.setItem(STORAGE_KEY, parsed.app_id);
            localStorage.setItem(EVERYMAN_SYNC_APP_ID_KEY, parsed.app_id);
            localStorage.removeItem(EVERYMAN_SYNC_OAUTH_DERIV_APP_ID_KEY);
            localStorage.removeItem(EVERYMAN_SYNC_OAUTH_CLIENT_ID_KEY);
            // eslint-disable-next-line no-console
            console.info('🖼️ [everyman-dtrader-sync] cookie_applied_to_ls', {
                source: 'root_cookie_everyman_dtrader_sync',
                mode: 'legacy_app_id',
                auth_flow: 'legacy',
                app_id_len: parsed.app_id.length,
                active_loginid_len: parsed.active_loginid.length,
            });
            return true;
        }

        localStorage.setItem(EVERYMAN_SYNC_OAUTH_DERIV_APP_ID_KEY, parsed.deriv_app_id);
        localStorage.setItem(EVERYMAN_SYNC_OAUTH_CLIENT_ID_KEY, parsed.oauth_client_id);
        localStorage.setItem(STORAGE_KEY, parsed.deriv_app_id);
        localStorage.setItem(EVERYMAN_SYNC_APP_ID_KEY, parsed.deriv_app_id);

        // eslint-disable-next-line no-console
        console.info('🖼️ [everyman-dtrader-sync] cookie_applied_to_ls', {
            source: 'root_cookie_everyman_dtrader_sync',
            mode: 'oauth2_client',
            auth_flow: 'oauth2',
            deriv_app_id_len: parsed.deriv_app_id.length,
            oauth_client_id_len: parsed.oauth_client_id.length,
            active_loginid_len: parsed.active_loginid.length,
        });
        return true;
    } catch {
        return false;
    }
};

const DEV_APP_ID = 113222;
const PROD_APP_ID = 114019;
const DEV_DTRADER_HOST = 'dev-dtrader.everymanmarkets.site';
const PROD_DTRADER_HOST = 'dtrader.everymanmarkets.site';

/**
 * App ID for the current dtrader hostname (dev vs prod subdomain).
 * Used so dev cookies are not applied on prod and vice versa.
 */
const getAppIdForDomain = (): string => {
    if (typeof window === 'undefined') return String(DEV_APP_ID);
    const host = window.location.hostname;
    if (host === PROD_DTRADER_HOST) return String(PROD_APP_ID);
    if (host === DEV_DTRADER_HOST) return String(DEV_APP_ID);
    return String(DEV_APP_ID);
};

/**
 * Resolve numeric WS `app_id`. On Everyman hosts only the sync cookie may populate `config.app_id`
 * (no URL override, no DEV/PROD hostname fallback).
 */
const getAppIdFromUrlOrConstant = (): string => {
    if (typeof window === 'undefined') return getAppIdForDomain();

    if (isEverymanDeploymentHost()) {
        try {
            const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
            if (stored && /^\d+$/.test(stored)) return stored;
        } catch {
            /* ignore */
        }
        // eslint-disable-next-line no-console
        console.error(
            '[everyman-dtrader] Missing valid sync cookie for config.app_id. Use the Everyman host after login or ensure everyman_dtrader_sync is set on the parent domain.'
        );
        return '';
    }

    try {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('app_id');
        if (fromUrl && /^\d+$/.test(fromUrl)) return fromUrl;
    } catch {
        // ignore
    }
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored && /^\d+$/.test(stored)) return stored;
    } catch {
        // ignore
    }
    return getAppIdForDomain();
};

/**
 * Resolve app_id (from URL if present, else APP_ID) and store in localStorage.
 * Only updates localStorage when the value is missing or different.
 * Call once at app bootstrap (lowest level); rest of the app reads from localStorage via getAppId().
 */
export const syncAppIdFromDomain = (): void => {
    if (typeof window === 'undefined') return;
    applyEverymanSyncCookieToLocalStorage();
    const resolved = getAppIdFromUrlOrConstant();
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored !== resolved) {
            localStorage.setItem(STORAGE_KEY, resolved);
        }
    } catch {
        // ignore
    }
};

/**
 * Returns app_id from localStorage (set once on page load by syncAppIdFromDomain).
 * If storage is empty (e.g. before sync ran), runs sync once then returns.
 * @deprecated Please use 'WebSocketUtils.getAppId' from '@deriv-com/utils' instead of this.
 */
export const getAppId = (): string => {
    if (typeof window === 'undefined') return getAppIdForDomain();
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored) return stored;
    } catch {
        // ignore
    }
    syncAppIdFromDomain();
    const resolved = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
    if (resolved) return resolved;
    if (isEverymanDeploymentHost()) return '';
    return getAppIdForDomain();
};

/**
 * V2 REST + OTP: `Deriv-App-ID` header (must match OAuth registration). From sync LS after V2 cookie; falls back to
 * `getAppId()` only when no V2 mirror key (e.g. older builds).
 */
export const getOAuthDerivAppIdFromEverymanSync = (): string => {
    if (typeof window === 'undefined') return '';
    try {
        const fromCookie = localStorage.getItem(EVERYMAN_SYNC_OAUTH_DERIV_APP_ID_KEY)?.trim();
        if (fromCookie && /^\d+$/.test(fromCookie)) return fromCookie;
    } catch {
        /* ignore */
    }
    return getAppId();
};

/** True when parent sync cookie applied V2 identifiers (PKCE client + Deriv-App-ID), not V1 `APP_ID` alone. */
export const isEverymanSyncV2OAuthClientMode = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        const id = localStorage.getItem(EVERYMAN_SYNC_OAUTH_CLIENT_ID_KEY)?.trim();
        return !!id;
    } catch {
        return false;
    }
};

/**
 * Returns the Deriv Bot base URL for the current app.
 */
export const getBotBaseUrl = (): string => {
    return 'https://bbot.botblitz.site';
};

// Same as root: single production WebSocket URL (ws.derivws.com), optional localStorage override
export const getSocketURL = (_is_wallets = false) => {
    const local_storage_server_url = window.localStorage.getItem('config.server_url');
    if (local_storage_server_url) return local_storage_server_url;
    return 'ws.derivws.com';
};

export const checkAndSetEndpointFromUrl = () => {
    if (isTestLink()) {
        const url_params = new URLSearchParams(location.search.slice(1));

        if (url_params.has('qa_server')) {
            const qa_server = url_params.get('qa_server') || '';

            url_params.delete('qa_server');

            if (/^(^(www\.)?qa[0-9]{1,4}\.deriv.dev|(.*)\.derivws\.com)$/.test(qa_server)) {
                localStorage.setItem('config.server_url', qa_server);
            }

            const params = url_params.toString();
            const hash = location.hash;

            location.href = `${location.protocol}//${location.hostname}${location.pathname}${
                params ? `?${params}` : ''
            }${hash || ''}`;

            return true;
        }
    }

    return false;
};

export const getDebugServiceWorker = () => {
    const debug_service_worker_flag = window.localStorage.getItem('debug_service_worker');
    if (debug_service_worker_flag) return !!parseInt(debug_service_worker_flag);

    return false;
};
